import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  Typography, 
  Button, 
  List, 
  Image, 
  Row, 
  Col,
  Badge,
  Empty,
  Space,
  Divider
} from "antd";
import { 
  DeleteOutlined, 
  ShoppingCartOutlined, 
  ShoppingOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined 
} from "@ant-design/icons";
import { useBasket } from "../components/BasketProvider";

const { Title, Text } = Typography;

const Basket = () => {
  const { basket, dispatch } = useBasket();
  const navigate = useNavigate();

  const handleRemove = (id) => dispatch({ type: "REMOVE_FROM_BASKET", payload: { id } });
  const handleClearBasket = () => dispatch({ type: "CLEAR_BASKET" });
  const handleCheckout = () => navigate("/checkout");

  const totalAmount = basket.reduce(
    (sum, item) => sum + (Number(item.price) || 0) * item.quantity,
    0
  );

  return (
    <div style={{ 
      padding: "20px", 
      maxWidth: "1200px", 
      margin: "0 auto",
      minHeight: "80vh"
    }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "24px" 
          }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
              style={{ marginRight: "16px" }}
            />
            <Title level={2} style={{ margin: 0 }}>
              <ShoppingCartOutlined /> Your Shopping Cart
              {basket.length > 0 && (
                <Badge 
                  count={basket.length} 
                  style={{ 
                    marginLeft: "12px", 
                    backgroundColor: "#1890ff" 
                  }}
                />
              )}
            </Title>
          </div>
        </Col>

        <Col xs={24} lg={16}>
          {basket.length > 0 ? (
            <Card
              style={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
              }}
              bodyStyle={{ padding: 0 }}
            >
              <List
                itemLayout="vertical"
                dataSource={basket}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f0f0f0"
                    }}
                    actions={[
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemove(item.id)}
                      >
                        Remove
                      </Button>
                    ]}
                  >
                    <Row align="middle" gutter={16} style={{ width: "100%" }}>
                      <Col xs={24} sm={6} md={4}>
                        <div style={{
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid #f0f0f0"
                        }}>
                          <Image
                            src={item.image}
                            alt={item.title}
                            style={{
                              width: "100%",
                              height: "auto",
                              aspectRatio: "1"
                            }}
                            preview={false}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={18} md={20}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Text strong style={{ fontSize: "16px" }}>
                            {item.title}
                          </Text>
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Text type="secondary">
                                Ksh {(Number(item.price) || 0).toFixed(2)} × {item.quantity}
                              </Text>
                            </Col>
                            <Col>
                              <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                                Ksh {((Number(item.price) || 0) * item.quantity).toFixed(2)}
                              </Text>
                            </Col>
                          </Row>
                        </Space>
                      </Col>
                    </Row>
                  </List.Item>
                )}
              />
            </Card>
          ) : (
            <Card
              style={{
                borderRadius: "12px",
                textAlign: "center",
                padding: "48px 24px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
              }}
            >
              <Empty
                image={<ShoppingCartOutlined style={{ fontSize: "64px", color: "#d9d9d9" }} />}
                description={
                  <Text type="secondary" style={{ fontSize: "16px" }}>
                    Your basket is empty
                  </Text>
                }
              >
                <Button 
                  type="primary" 
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate("/")}
                >
                  Continue Shopping
                </Button>
              </Empty>
            </Card>
          )}
        </Col>

        {basket.length > 0 && (
          <Col xs={24} lg={8}>
            <Card
              title={
                <Space>
                  <CheckCircleOutlined />
                  <Text strong>Order Summary</Text>
                </Space>
              }
              style={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                position: "sticky",
                top: "20px"
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                <Row justify="space-between">
                  <Text>Items ({basket.length})</Text>
                  <Text strong>Ksh {totalAmount.toFixed(2)}</Text>
                </Row>
                
                <Divider style={{ margin: "8px 0" }} />
                
                <Row justify="space-between">
                  <Text strong style={{ fontSize: "18px" }}>Total</Text>
                  <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                    Ksh {totalAmount.toFixed(2)}
                  </Title>
                </Row>
                
                <Divider style={{ margin: "8px 0" }} />
                
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handleCheckout}
                    style={{
                      height: "48px",
                      fontSize: "16px",
                      fontWeight: "600"
                    }}
                  >
                    Proceed to Checkout
                  </Button>
                  
                  <Button
                    danger
                    type="default"
                    size="large"
                    block
                    icon={<DeleteOutlined />}
                    onClick={handleClearBasket}
                  >
                    Clear All Items
                  </Button>
                  
                  <Button
                    type="text"
                    block
                    onClick={() => navigate("/")}
                  >
                    Continue Shopping
                  </Button>
                </Space>
                
                <div style={{ 
                  padding: "12px", 
                  backgroundColor: "#f6ffed",
                  borderRadius: "8px",
                  border: "1px solid #b7eb8f"
                }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: "4px" }} />
                    Free shipping on orders over Ksh 5,000
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Basket;